import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { styled } from "@mui/system";

const StyledTab = styled(Tab)({
  textTransform: "none",
  fontWeight: "bold",
});

const LocDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loc, setLoc] = useState(null);
  const [customLoc, setCustomLoc] = useState(null);
  const [activeTab, setActiveTab] = useState(8);
  const token = localStorage.getItem("access_token");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const fetchProjectDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/${projectId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch project details");

      const data = await response.json();
      const LOC = data?.clocReport?.SUM?.code ?? 0;
      const customLOC = data?.customClocReport?.SUM?.code ?? 0;

      setLoc(LOC);
      setCustomLoc(customLOC);
      setProject(data);
    } catch (error) {
      console.error("Error fetching project details:", error);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchProjectDetails();
  }, [fetchProjectDetails]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderCodeDetailsTable = (report, totalLOC, is_limit_exceeded) => {
    if (!report) return null;

    const filteredEntries = Object.entries(report).filter(
      ([key, value]) => key !== "SUM" && key !== "header" && value?.code
    );

    return (
      <>
        {is_limit_exceeded && (
          <Typography
            sx={{
              mt: 2,
              display: "inline-block",
              border: "1px solid #ccc",
              backgroundColor: "#fff3cd",
              borderRadius: "8px",
              color: "#856404",
              fontWeight: 500,
              ml: 1,
              px: 1.5,
              py: 0.5,
              fontSize: {
                xs: "0.8rem",
                sm: "0.9rem",
                md: "1rem",
              },
            }}
          >
            Code limit exceeded.
          </Typography>
        )}

        <TableContainer
          component={Paper}
          sx={{ mt: 3, backgroundColor: isDarkMode ? "#121212" : "#fff" }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{ backgroundColor: isDarkMode ? "blue" : "#f5f5f5" }}
              >
                <TableCell sx={{ color: isDarkMode ? "#fff" : "inherit" }}>
                  <strong>Language</strong>
                </TableCell>
                <TableCell sx={{ color: isDarkMode ? "#fff" : "inherit" }}>
                  <strong>Lines of Code</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries.map(([language, details]) => (
                <TableRow key={language}>
                  <TableCell sx={{ color: isDarkMode ? "#fff" : "inherit" }}>
                    {language}
                  </TableCell>
                  <TableCell sx={{ color: isDarkMode ? "#fff" : "inherit" }}>
                    {details.code}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow
                sx={{
                  backgroundColor: isDarkMode ? "blue" : "#e0e0e0",
                  fontWeight: "bold",
                }}
              >
                <TableCell sx={{ color: isDarkMode ? "#fff" : "#000" }}>
                  <strong>Total LOC</strong>
                </TableCell>
                <TableCell sx={{ color: isDarkMode ? "#fff" : "#000" }}>
                  <strong>{totalLOC}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <CircularProgress />
      </Box>
    );

  if (error) return <Typography color="error">Failed to load data.</Typography>;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        backgroundColor: isDarkMode ? "#121212" : "#f5f5f5",
        minHeight: "94vh",
        pb: 15,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Paper sx={{ p: 3, maxWidth: 800, width: "100%" }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Lines of Code - File Breakdown
          </Typography>

          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: "column",
              textAlign: "left",
              color: isDarkMode ? "#fff" : "inherit",
            }}
          >
            <Typography variant="h6">
              Project: {project.name || "N/A"}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontSize: "1rem" }}>
              Description: {project.description || "N/A"}
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "1rem" }}>
              Code Language: {project.codeLanguage || "N/A"}
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "1rem" }}>
              Created At:{" "}
              {project.createdAt
                ? new Date(project.createdAt * 1000).toLocaleString()
                : "N/A"}
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "1rem" }}>
              Main File: {project.mainFileName || "N/A"}
            </Typography>
            {project.clientRepoUrl && (
              <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                Repository URL:{" "}
                <a
                  href={project.clientRepoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: isDarkMode ? "#ffffff" : "black",
                    textDecoration: "none",
                  }}
                >
                  {project.clientRepoUrl}
                </a>
              </Typography>
            )}
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mb: 3,
              "& .MuiTabs-indicator": {
                height: "3px",
                backgroundColor: "#1976d2",
              },
            }}
          >
            <StyledTab label="Lines Of Code" value={8} />
            {project?.customClocReport && (
              <StyledTab label="Custom Lines Of Code" value={9} />
            )}
          </Tabs>

          {activeTab === 8 &&
            renderCodeDetailsTable(
              project.clocReport,
              loc,
              project?.clocReport?.is_limit_exceeded
            )}

          {activeTab === 9 &&
            renderCodeDetailsTable(
              project.customClocReport,
              customLoc,
              project?.customClocReport?.is_limit_exceeded
            )}

          <Button
            variant="contained"
            className="athrv-dash-primary-button"
            sx={{ mt: 3 }}
            onClick={() => navigate(-1)}
          >
            Back to Project
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default LocDetails;
